import { fetchJudgeMeGlobalOverview } from 'api/product';
import IconStarFilled from 'assets/icons/star-filled.svg';
import IconStarHalf from 'assets/icons/star-half.svg';
import IconStar from 'assets/icons/star.svg';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';

interface JudgeMeOverviewProps {
  className?: string;
}

/**
 * Displays global JudgeMe ratings with star icons and rating count
 */
const JudgeMeOverview: React.FC<JudgeMeOverviewProps> = ({ className }) => {
  const { formatNumber } = useIntl();
  const { data, isValidating } = useSWR(
    'JudgeMeGlobalOverview',
    fetchJudgeMeGlobalOverview
  );

  if (!data?.rating || !data?.count) return null;

  const rating = Number(data.rating) || 0;
  let fullStars = Math.floor(rating);
  let halfStar = 0;
  let offStars = 0;

  if (rating % 1 >= 0.25 && rating % 1 <= 0.75) {
    halfStar = 1;
    offStars = 5 - fullStars - halfStar;
  } else if (rating % 1 > 0.74) {
    fullStars += 1;
    offStars = 5 - fullStars;
  } else {
    offStars = 5 - fullStars;
  }

  return (
    <>
      {isValidating ? (
        <div
          className={cn(
            className,
            'h-5 w-2/3 animate-pulse rounded-xl bg-grey-100'
          )}
          data-test="judge-me-overview-loading"
        />
      ) : (
        <div
          className={cn(
            className,
            'flex items-center gap-1 text-sm text-text-light-bg-secondary'
          )}
          data-test="judge-me-overview"
        >
          <div className="flex items-center text-other-rating">
            {[...Array(fullStars)].map((_, i) => (
              <IconStarFilled key={i} className="h-5 w-5 shrink-0" />
            ))}
            {halfStar ? (
              <IconStarHalf className="h-5 w-5 shrink-0" />
            ) : null}
            {[...Array(offStars)].map((_, i) => (
              <IconStar
                key={i + fullStars + halfStar}
                className="h-5 w-5 shrink-0"
              />
            ))}
          </div>
          <span
            className="flex translate-y-[0.5px] items-center leading-none"
            data-test="judge-me-overview-count"
          >
            <FormattedMessage
              id="landingpage:home:judge-me-values"
              values={{
                rating: formatNumber(data.rating, {
                  maximumFractionDigits: 1,
                }),
                count: formatNumber(data.count),
              }}
            />
          </span>
        </div>
      )}
    </>
  );
};

export default JudgeMeOverview;
